import React from "react";
import { Link } from "react-router-dom";
import { ChevronRight, Upload, Palette, Shield, Lock, HelpCircle, ChevronDown, ArrowRight } from "lucide-react";
import useSEO from "@/hooks/useSEO";

const EXAMPLE_CARDS = "/cards";

const SAMPLE_DOGS = [
  { name: "Max", sport: "Football", position: "Quarterback", style: "Retro", img: `${EXAMPLE_CARDS}/max-football-qb.jpg` },
  { name: "Biscuit", sport: "Basketball", position: "Point Guard", style: "Action", img: `${EXAMPLE_CARDS}/biscuit-basketball-pg.jpg` },
  { name: "Rex", sport: "Baseball", position: "Pitcher", style: "Portrait", img: `${EXAMPLE_CARDS}/rex-baseball-pitcher.jpg` },
  { name: "Storm", sport: "Hockey", position: "Goalie", style: "Action", img: `${EXAMPLE_CARDS}/storm-hockey-goalie.jpg` },
  { name: "Duke", sport: "Soccer", position: "Striker", style: "Retro", img: `${EXAMPLE_CARDS}/duke-soccer-striker.jpg` },
  { name: "Nugget", sport: "Basketball", position: "Shooting Guard", style: "Action", img: `${EXAMPLE_CARDS}/nugget-basketball-sg.jpg` },
];

const BEFORE_AFTER = [
  { name: "Max", breed: "Golden Retriever", sport: "Football", position: "Quarterback", before: "https://images.unsplash.com/photo-1519619213474-27d00cb019cb?w=400&h=500&fit=crop", after: `${EXAMPLE_CARDS}/max-football-qb.jpg` },
  { name: "Biscuit", breed: "French Bulldog", sport: "Basketball", position: "Point Guard", before: "https://images.pexels.com/photos/10497109/pexels-photo-10497109.jpeg?auto=compress&cs=tinysrgb&w=400&h=500&fit=crop", after: `${EXAMPLE_CARDS}/biscuit-basketball-pg.jpg` },
  { name: "Rex", breed: "German Shepherd", sport: "Baseball", position: "Pitcher", before: "https://images.unsplash.com/photo-1636924043205-0dd7f906e954?w=400&h=500&fit=crop", after: `${EXAMPLE_CARDS}/rex-baseball-pitcher.jpg` },
];

const SPORTS = [
  { name: "Football", dog: "Max", position: "Quarterback", img: `${EXAMPLE_CARDS}/max-football-qb.jpg` },
  { name: "Basketball", dog: "Biscuit", position: "Point Guard", img: `${EXAMPLE_CARDS}/biscuit-basketball-pg.jpg` },
  { name: "Baseball", dog: "Rex", position: "Pitcher", img: `${EXAMPLE_CARDS}/rex-baseball-pitcher.jpg` },
  { name: "Hockey", dog: "Storm", position: "Goalie", img: `${EXAMPLE_CARDS}/storm-hockey-goalie.jpg` },
  { name: "Soccer", dog: "Duke", position: "Striker", img: `${EXAMPLE_CARDS}/duke-soccer-striker.jpg` },
];

const FAQS = [
  { q: "Will it actually look like MY dog?", a: "Yes. Our AI uses your photo as a direct reference. The better the photo, the better the match. Clear, well-lit face shots work best." },
  { q: "What do I get?", a: "The Digital Legend Pack includes a custom HD sports card, mobile wallpaper, social-media-ready image, and a print-ready file (3000x4200px) — all delivered instantly with no watermark." },
  { q: "How long does it take?", a: "About 15 seconds for the preview. Instantly downloadable after payment." },
  { q: "Can I get a refund?", a: "We offer one free regeneration if you're not happy. Refunds available within 24 hours if the card doesn't resemble your dog." },
  { q: "What sports are available?", a: "Football, basketball, baseball, hockey, and soccer." },
  { q: "Can I put my cat on a card?", a: "Absolutely. Any pet works." },
  { q: "Are real team logos used?", a: "No. All team branding is original and fictional \u2014 your dog plays for a totally unique team." },
  { q: "How are my photos used?", a: "Your photo is used only to generate your card and is deleted from our servers within 24 hours. We never share or sell your images." },
];

function FAQItem({ q, a }) {
  const [open, setOpen] = React.useState(false);
  return (
    <div className="border border-[var(--color-border)] rounded-[12px] bg-[var(--color-white)] overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-6 py-4 text-left"
        data-testid={`faq-toggle-${q.slice(0, 20).replace(/\s+/g, '-').toLowerCase()}`}
      >
        <span className="font-body font-medium text-[var(--color-text-dark)] pr-4">{q}</span>
        <ChevronDown size={18} className={`text-[var(--color-text-light)] shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="px-6 pb-4 animate-fade-in">
          <p className="font-body text-sm text-[var(--color-text-mid)] leading-relaxed">{a}</p>
        </div>
      )}
    </div>
  );
}

export default function LandingPage() {
  useSEO({ path: "/" });
  return (
    <div data-testid="landing-page">
      {/* HERO */}
      <section
        className="relative overflow-hidden hero-texture"
        style={{ backgroundColor: "var(--color-navy)" }}
        data-testid="hero-section"
      >
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-10 md:py-12">
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.2fr] gap-8 lg:gap-6 items-center w-full">
            <div className="animate-slide-up">
              <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl tracking-tight uppercase leading-none text-white mb-4">
                Turn your dog into a{" "}
                <span style={{ color: "var(--color-gold)" }}>sports legend</span>
              </h1>
              <p className="font-body text-lg text-white/70 mb-5 max-w-lg leading-relaxed">
                Upload a photo. Pick a sport. Get a professional trading card
                starring your dog. In 15 seconds.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/create"
                  className="font-heading tracking-widest uppercase text-lg px-8 py-4 rounded-[8px] transition-colors text-center btn-mobile-full"
                  style={{ backgroundColor: "var(--color-gold)", color: "var(--color-navy)" }}
                  data-testid="hero-cta-create"
                >
                  Create your card free
                </Link>
                <a
                  href="#gallery"
                  className="font-heading tracking-widest uppercase text-lg px-8 py-4 rounded-[8px] transition-colors text-center border-2 border-white/30 text-white hover:bg-white/10 btn-mobile-full"
                  data-testid="hero-cta-examples"
                >
                  See the cards
                </a>
              </div>
              <p className="font-body text-sm text-white/50 mt-5">As seen on Facebook &amp; TikTok &middot; Free preview &middot; Love it or your money back.</p>
            </div>

            {/* Hero before/after image — desktop: right side, mobile: below text */}
            <div className="flex justify-center lg:justify-end items-center self-center">
              <img
                src="/hero-before-after.png"
                alt="Before and after — real dogs transformed into professional sports trading cards"
                className="w-[90%] lg:w-[480px] object-contain"
              />
            </div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="py-20 md:py-32" style={{ backgroundColor: "var(--color-navy)" }} data-testid="how-it-works-section">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <h2 className="font-heading text-3xl sm:text-4xl tracking-tight uppercase text-center text-white mb-4">
            How it works
          </h2>
          <p className="font-body text-white/60 text-center mb-16 max-w-xl mx-auto">
            Free preview in 15 seconds. No account needed. No credit card required.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-12">
            {[
              { icon: <Upload size={32} />, title: "Upload your photo", desc: "Snap or upload a clear photo of your dog. Any breed, any angle." },
              { icon: <Palette size={32} />, title: "Choose sport + position", desc: "Football QB? Basketball center? Pick the sport and position that fits." },
              { icon: null, title: "Get your card in seconds", desc: "Our AI generates a professional trading card featuring your actual dog.", isCard: true },
            ].map((step, i) => (
              <div
                key={i}
                className="rounded-[12px] p-8 text-center card-tilt"
                style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.08)", animationDelay: `${i * 100}ms` }}
              >
                {step.isCard ? (
                  <div className="w-16 h-[88px] mx-auto mb-5 rounded-[6px] overflow-hidden border-2 border-[var(--color-gold)] shadow-lg">
                    <img src={`${EXAMPLE_CARDS}/nugget-basketball-sg.jpg`} alt="Example card" className="w-full h-full object-cover" />
                  </div>
                ) : (
                  <div
                    className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-5"
                    style={{ backgroundColor: "var(--color-navy)", border: "2px solid var(--color-gold)", color: "var(--color-gold)" }}
                  >
                    {step.icon}
                  </div>
                )}
                <div className="font-accent text-xs tracking-widest uppercase mb-3" style={{ color: "var(--color-gold)" }}>
                  Step {i + 1}
                </div>
                <h3 className="font-heading text-xl uppercase tracking-wide text-white mb-3">{step.title}</h3>
                <p className="font-body text-sm text-white/50 leading-relaxed">{step.desc}</p>
              </div>
            ))}
          </div>
          <div className="text-center mt-12">
            <Link
              to="/create"
              className="inline-block font-heading tracking-widest uppercase text-lg px-10 py-4 rounded-[8px] transition-colors"
              style={{ backgroundColor: "var(--color-gold)", color: "var(--color-navy)" }}
              data-testid="how-it-works-cta"
            >
              Start for free
            </Link>
          </div>
        </div>
      </section>

      {/* BEFORE / AFTER */}
      <section className="py-20 md:py-32" style={{ backgroundColor: "var(--color-white)" }} data-testid="before-after-section">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <h2 className="font-heading text-3xl sm:text-4xl tracking-tight uppercase text-center text-[var(--color-text-dark)] mb-4">
            From good boy to <span style={{ color: "var(--color-gold)" }}>MVP</span>
          </h2>
          <p className="font-body text-[var(--color-text-mid)] text-center mb-16 max-w-xl mx-auto">
            Any breed. Any sport. Your dog, immortalized.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-10">
            {BEFORE_AFTER.map((item) => (
              <div key={item.name} className="text-center">
                <div className="flex items-center gap-3 justify-center mb-4">
                  {/* Before */}
                  <div className="w-[130px] sm:w-[150px] shrink-0">
                    <div className="rounded-[12px] border border-[var(--color-border)] overflow-hidden shadow-sm">
                      <img src={item.before} alt={`${item.breed} photo`} className="w-full aspect-square object-cover" loading="lazy" />
                    </div>
                    <p className="font-accent text-[10px] tracking-widest uppercase mt-2 text-[var(--color-text-light)]">Before</p>
                  </div>
                  {/* Arrow */}
                  <div className="shrink-0">
                    <ArrowRight size={22} style={{ color: "var(--color-gold)" }} />
                  </div>
                  {/* After */}
                  <div className="w-[130px] sm:w-[150px] shrink-0">
                    <div className="trading-card-frame card-tilt overflow-hidden">
                      <img src={item.after} alt={`${item.name} trading card`} className="w-full aspect-[2/3] object-cover" loading="lazy" />
                    </div>
                    <p className="font-accent text-[10px] tracking-widest uppercase mt-2" style={{ color: "var(--color-gold)" }}>After</p>
                  </div>
                </div>
                <p className="font-heading text-lg uppercase tracking-wider text-[var(--color-text-dark)]">{item.name}</p>
                <p className="font-body text-xs text-[var(--color-text-mid)]">{item.breed} &middot; {item.sport} {item.position}</p>
              </div>
            ))}
          </div>
          <div className="text-center mt-12">
            <Link
              to="/create"
              className="inline-block font-heading tracking-widest uppercase text-lg px-8 py-4 rounded-[8px] transition-colors"
              style={{ backgroundColor: "var(--color-gold)", color: "var(--color-navy)" }}
              data-testid="before-after-cta"
            >
              Try it with your dog
            </Link>
          </div>
        </div>
      </section>

      {/* EXAMPLE GALLERY */}
      <section id="gallery" className="py-20 md:py-32" data-testid="gallery-section">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <h2 className="font-heading text-3xl sm:text-4xl tracking-tight uppercase text-center text-[var(--color-text-dark)] mb-4">
            Hall of Legends
          </h2>
          <p className="font-body text-[var(--color-text-mid)] text-center mb-16 max-w-xl mx-auto">
            Every card is 100% original. Every team is fictional. Every dog is a legend.
          </p>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-6 md:gap-8">
            {SAMPLE_DOGS.map((dog) => (
              <div key={dog.name}>
                <Link to="/create" className="group relative trading-card-frame card-tilt cursor-pointer block">
                  <img
                    src={dog.img}
                    alt={dog.name}
                    className="w-full aspect-[2/3] object-cover"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[var(--color-navy)] via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col justify-end p-4">
                    <p className="font-heading text-white text-xl tracking-wider uppercase">{dog.name}</p>
                    <p className="font-accent text-xs" style={{ color: "var(--color-gold)" }}>{dog.sport} &middot; {dog.position}</p>
                  </div>
                </Link>
                <Link to="/create" className="block text-center mt-2 font-body text-xs text-[var(--color-text-light)] hover:text-[var(--color-gold)] transition-colors">
                  Make yours &rarr;
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SPORTS SELECTOR TEASER */}
      <section className="py-20 md:py-32" style={{ backgroundColor: "var(--color-navy)" }} data-testid="sports-teaser-section">
        <div className="max-w-7xl mx-auto px-4 md:px-8 text-center">
          <h2 className="font-heading text-3xl sm:text-4xl tracking-tight uppercase text-white mb-4">
            What sport does your dog dominate?
          </h2>
          <p className="font-body text-base mb-14" style={{ color: "var(--color-gold)" }}>
            Click your sport to get started &mdash; free preview, no account needed
          </p>
          <div className="flex flex-wrap justify-center gap-6 md:gap-8">
            {SPORTS.map((sport) => (
              <Link
                to="/create"
                key={sport.name}
                className="group block w-[160px] sm:w-[200px] rounded-[12px] overflow-hidden transition-all duration-300 hover:scale-105"
                style={{ background: "rgba(255,255,255,0.06)", border: "2px solid rgba(255,255,255,0.08)" }}
                onMouseEnter={(e) => { e.currentTarget.style.borderColor = "var(--color-gold)"; e.currentTarget.style.boxShadow = "0 0 24px rgba(245,166,35,0.25)"; }}
                onMouseLeave={(e) => { e.currentTarget.style.borderColor = "rgba(255,255,255,0.08)"; e.currentTarget.style.boxShadow = "none"; }}
                data-testid={`sport-teaser-${sport.name.toLowerCase()}`}
              >
                <div className="px-3 pt-3 pb-0 overflow-hidden">
                  <img
                    src={sport.img}
                    alt={`${sport.dog} ${sport.name} card`}
                    className="w-full aspect-[2/3] object-cover rounded-[8px] transition-transform duration-300 group-hover:rotate-1 group-hover:scale-[1.03]"
                    style={{ transform: "rotate(-1.5deg)" }}
                    loading="lazy"
                  />
                </div>
                <div className="px-3 pt-3 pb-4">
                  <p className="font-heading text-xl uppercase tracking-wider" style={{ color: "var(--color-gold)" }}>{sport.name}</p>
                  <p className="font-body text-xs text-white/50 mt-0.5">{sport.dog} &middot; {sport.position}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section className="py-20 md:py-32" style={{ backgroundColor: "var(--color-white)" }} data-testid="pricing-section">
        <div className="max-w-3xl mx-auto px-4 md:px-8 text-center">
          {/* Guarantee block */}
          <div className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full mb-8" style={{ backgroundColor: "rgba(46,204,113,0.08)", border: "1px solid rgba(46,204,113,0.2)" }}>
            <Shield size={16} style={{ color: "var(--color-success)" }} />
            <span className="font-body text-sm text-[var(--color-text-dark)]">Free preview. Love it or we'll fix it. Refund if you don't.</span>
          </div>
          <h2 className="font-heading text-3xl sm:text-4xl tracking-tight uppercase text-[var(--color-text-dark)] mb-4">
            Your Dog Is Already a Legend. Now Get Them a Card.
          </h2>
          <p className="font-body text-[var(--color-text-mid)] mb-12">
            Free preview &mdash; see your card before you pay
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-[var(--color-cream)] rounded-[12px] border border-[var(--color-border)] p-8">
              <div className="font-accent text-xs tracking-widest uppercase mb-2" style={{ color: "var(--color-gold)" }}>Digital Legend Pack</div>
              <div className="font-heading text-5xl text-[var(--color-text-dark)] mb-2"><span className="text-3xl line-through text-[var(--color-text-light)] mr-2">$29</span>$17</div>
              <p className="font-body text-sm text-[var(--color-text-mid)] mb-6">Your Dog's Digital Starter Kit</p>
              <ul className="text-left space-y-3 mb-6">
                {["Custom Dog Sports Card (HD)", "Mobile Optimized Wallpaper", "Social Media Ready", "Print-Ready File (3000x4200px)", "Instant Delivery", "No Watermark"].map(f => (
                  <li key={f} className="flex items-center gap-2 font-body text-sm text-[var(--color-text-mid)]">
                    <ChevronRight size={14} style={{ color: "var(--color-success)" }} />{f}
                  </li>
                ))}
              </ul>
              <Link
                to="/create"
                className="block w-full font-heading tracking-widest uppercase text-center py-3 rounded-[8px] transition-colors"
                style={{ backgroundColor: "var(--color-gold)", color: "var(--color-navy)" }}
                data-testid="pricing-cta-digital"
              >
                Get Started
              </Link>
            </div>
            <div className="bg-[var(--color-cream)] rounded-[12px] border-2 border-[var(--color-gold)] p-8 relative">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-0.5 rounded-full font-accent text-xs" style={{ backgroundColor: "var(--color-gold)", color: "var(--color-navy)" }}>
                BEST VALUE
              </div>
              <div className="font-accent text-xs tracking-widest uppercase mb-2" style={{ color: "var(--color-gold)" }}>Collector's Edition</div>
              <div className="font-heading text-5xl text-[var(--color-text-dark)] mb-2"><span className="text-3xl line-through text-[var(--color-text-light)] mr-2">$54</span>$42</div>
              <p className="font-body text-sm text-[var(--color-text-mid)] mb-6">Digital download + premium print</p>
              <ul className="text-left space-y-3 mb-6">
                {["Everything in Digital Legend Pack", "Premium 8x10 matte art print", "Ready to frame", "Ships in 5-7 days", "Perfect as a gift"].map(f => (
                  <li key={f} className="flex items-center gap-2 font-body text-sm text-[var(--color-text-mid)]">
                    <ChevronRight size={14} style={{ color: "var(--color-success)" }} />{f}
                  </li>
                ))}
              </ul>
              <Link
                to="/create"
                className="block w-full font-heading tracking-widest uppercase text-center py-3 rounded-[8px] transition-colors"
                style={{ backgroundColor: "var(--color-gold)", color: "var(--color-navy)" }}
                data-testid="pricing-cta-print"
              >
                Get Started
              </Link>
            </div>
          </div>
          <div className="flex items-center justify-center gap-6 mt-8">
            <div className="flex items-center gap-2 text-[var(--color-text-light)] font-body text-xs">
              <Lock size={14} /> Stripe Secure Checkout
            </div>
            <div className="flex items-center gap-2 text-[var(--color-text-light)] font-body text-xs">
              <Shield size={14} /> 256-bit SSL
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 md:py-32" data-testid="faq-section">
        <div className="max-w-3xl mx-auto px-4 md:px-8">
          <h2 className="font-heading text-3xl sm:text-4xl tracking-tight uppercase text-center text-[var(--color-text-dark)] mb-4">
            Things people actually ask
          </h2>
          <p className="font-body text-[var(--color-text-mid)] text-center mb-12">
            <HelpCircle size={16} className="inline mr-1" style={{ color: "var(--color-gold)" }} />
            Everything you need to know
          </p>
          <div className="space-y-3">
            {FAQS.map((faq) => (
              <FAQItem key={faq.q} q={faq.q} a={faq.a} />
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-20 md:py-32 hero-texture" style={{ backgroundColor: "var(--color-navy)" }}>
        <div className="max-w-3xl mx-auto px-4 md:px-8 text-center">
          <h2 className="font-heading text-4xl sm:text-5xl tracking-tight uppercase text-white mb-4">
            Ready to make your dog a <span style={{ color: "var(--color-gold)" }}>legend</span>?
          </h2>
          <p className="font-body text-white/60 mb-8">The only sports card that actually looks like your dog.</p>
          <Link
            to="/create"
            className="inline-block font-heading tracking-widest uppercase text-lg px-10 py-4 rounded-[8px] transition-colors"
            style={{ backgroundColor: "var(--color-gold)", color: "var(--color-navy)" }}
            data-testid="final-cta"
          >
            Create your free card
          </Link>
        </div>
      </section>
    </div>
  );
}
